import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ApplicationError } from 'app/_errors/application.error';
import { environment } from 'environments/environment';
import { AuthService } from './_services/auth.service';
import { NotificationService } from './_services/notification.service';
import { LoggerService } from './_services/logger.service';
import { Globals } from './_globals';
import { CryptoService } from './_services/crypto.service';
import { LoginAuthenticationError } from 'app/_errors/login-authentication.error';
import { RefreshAuthenticationError } from 'app/_errors/refresh-authentication.error';


// tslint:disable:cyclomatic-complexity
@Injectable()
export class ErrorsHandler implements ErrorHandler {
  private readonly chunkLoadRetryStorageKey = 'skylark.chunk-load-last-retry-at';
  private readonly chunkLoadRetryTtlMs = 5 * 60 * 1000;
  private notificationService!: NotificationService;
  private authService!: AuthService;
  private router!: Router;
  private ngZone!: NgZone;
  private fatalClientErrorInProgress = false;

  constructor(
    // Because the ErrorHandler is created before the providers, we'll have to use the Injector to get them.
    private injector: Injector,
    private loggerService: LoggerService,
    private cryptoService: CryptoService,
    private globals: Globals,
  ) { }

  public handleError(error: Error | HttpErrorResponse) {
    this.injectDependencyServices();
    const normalizedError = this.normalizeError(error);
    const classifiedError = this.classifyError(normalizedError);
    return this.handleClassifiedError(classifiedError, normalizedError);
  }

  private injectDependencyServices() {
    this.notificationService = this.injector.get<NotificationService>(NotificationService);
    this.authService = this.injector.get<AuthService>(AuthService);
    this.router = this.injector.get<Router>(Router);
    this.ngZone = this.injector.get<NgZone>(NgZone);
  }

  private normalizeError(error: any) {
    // Angular/Zone can pass unhandled Promise rejections as wrapper objects.
    // Unwrap the original rejected error so the rest of the handler can classify it normally.
    const maybeUnhandledRejection = error as { rejection?: any };
    const normalizedError = maybeUnhandledRejection.rejection ? maybeUnhandledRejection.rejection : error;

    // Other formats of errors encountered can be normalized here as and when they are encountered,
    // e.g. potentially ngOriginalError, ZoneAwareError, recursively wrapped promises...

    return normalizedError;
  }

  private classifyError(error: any):
    | { kind: 'ignored_third_party'; error: HttpErrorResponse }
    | { kind: 'application'; error: ApplicationError; title: string; message: string }
    | { kind: 'login_authentication'; error: LoginAuthenticationError; title: string; message: string }
    | { kind: 'refresh_authentication'; error: RefreshAuthenticationError; title: string; message: string }
    | { kind: 'offline'; error: HttpErrorResponse; url: string; title: string; message: string }
    | { kind: 'timeout'; error: HttpErrorResponse; url: string; title: string; message: string }
    | { kind: 'http'; error: HttpErrorResponse; url: string; title: string; message: string }
    | { kind: 'fatal_client'; error: any } {

    if (error instanceof ApplicationError) {
      return {
        kind: 'application',
        error,
        title: 'Error: Application Error',
        message: error.userMessage
      };
    }

    if (error instanceof LoginAuthenticationError) {
      return {
        kind: 'login_authentication',
        error,
        title: 'Error: Login Error',
        message: 'Please check username and password and login again to continue.'
      };
    }

    if (error instanceof RefreshAuthenticationError) {
      return {
        kind: 'refresh_authentication',
        error,
        title: 'Error: Authentication Error',
        message: 'Authentication failed. Please login again to continue.'
      };
    }

    if (error instanceof HttpErrorResponse && this.shouldIgnoreHttpError(error)) {
      return { kind: 'ignored_third_party', error };
    }

    if (error instanceof HttpErrorResponse) {
      return this.classifyHttpError(error);
    }

    return { kind: 'fatal_client', error };
  }

  // Classify any first-party backend error surfaced by Angular's HttpClient.
  // This includes transport/network failures, server-side failures, and expected API
  // error responses with HTTP status codes such as 400/401/404/500.
  private classifyHttpError(error: HttpErrorResponse):
    | { kind: 'offline'; error: HttpErrorResponse; url: string; title: string; message: string }
    | { kind: 'timeout'; error: HttpErrorResponse; url: string; title: string; message: string }
    | { kind: 'http'; error: HttpErrorResponse; url: string; title: string; message: string } {

    const url = error.url ? error.url.toLowerCase() : 'unknown';
    let status = error.status.toString();
    let message = error.message;

    if (!navigator.onLine) {
      return { kind: 'offline', error, url, title: '', message: 'No Internet Connection' };
    }

    if (error.status === 0) {
      // No usable HTTP response was received at all, so treat it as a connection issue.
      // Common causes include network failure, server unreachable, CORS/preflight failure,
      // browser-level transport problems, or local/company firewall or blocking policies.
      status = 'Connection Error';
      message = 'Unable to reach server. Please check your internet connection or try again later.';
    } else if (error.error) {
      // HttpErrorResponse is Angular's outer wrapper around the failed request.
      // The nested error.error value is usually the backend's JSON error payload.
      const backendError = error.error;

      if (backendError.status) {
        status = backendError.status;
      }

      if (backendError.status === 401) {
        return {
          kind: 'timeout',
          error,
          url,
          title: 'Timeout',
          message: 'Your session has timed out, please login again.'
        };
      }

      if (backendError.detail && backendError.detail.includes('constraint [ux_user_email]')) {
        return { kind: 'http', error, url, title: `Error: ${status}`, message: 'Email address already in use' };
      }

      if (backendError.detail && backendError.detail.includes('constraint [ux_user_login]')) {
        return { kind: 'http', error, url, title: `Error: ${status}`, message: 'Login already in use' };
      }

      if (backendError.title === 'Method argument not valid') {
        return {
          kind: 'http',
          error,
          url,
          title: 'Error: Input Validation',
          message: Array.isArray(backendError.fieldErrors) && backendError.fieldErrors.length
            ? backendError.fieldErrors.map((issue: { message: any; }) => issue.message).join('<br/>')
            : 'Input validation error'
        };
      }

      message = backendError.title || backendError.message || backendError.error || error.message;
    }

    return { kind: 'http', error, url, title: `Error: ${status}`, message };
  }

  // The error has already been classified and any user-facing title/message has been prepared.
  // This method handles the resulting notification plus any additional side effects such as
  // logout, logging, or fallback navigation for certain error kinds.
  private handleClassifiedError(
    classifiedError:
      | { kind: 'ignored_third_party'; error: HttpErrorResponse }
      | { kind: 'application'; error: ApplicationError; title: string; message: string }
      | { kind: 'login_authentication'; error: LoginAuthenticationError; title: string; message: string }
      | { kind: 'refresh_authentication'; error: RefreshAuthenticationError; title: string; message: string }
      | { kind: 'offline'; error: HttpErrorResponse; url: string; title: string; message: string }
      | { kind: 'timeout'; error: HttpErrorResponse; url: string; title: string; message: string }
      | { kind: 'http'; error: HttpErrorResponse; url: string; title: string; message: string }
      | { kind: 'fatal_client'; error: any },
    originalError: any
  ) {
    const userId = this.globals.user && this.globals.user.id ? this.globals.user.id : 'unknown';

    if (classifiedError.kind === 'ignored_third_party') {
      console.warn('3rd Party URL issue', classifiedError.error);
      return null;
    }

    if (classifiedError.kind === 'application') {
      console.error(classifiedError.error.name, {
        applicationError: classifiedError.error,
        originalError: classifiedError.error.originalError
      });

      this.loggerService.logToServer(
        userId,
        this.getApplicationErrorLogMessage(classifiedError.error)
      );

      return this.notificationService.notify('danger', classifiedError.title, classifiedError.message);
    }

    if (classifiedError.kind === 'offline') {
      return this.notificationService.notify('danger', classifiedError.title, classifiedError.message);
    }

    if (classifiedError.kind === 'login_authentication') {
      console.error('Login Authentication Error');
      this.ngZone.run(() => this.authService.handleUiLogout());

      return this.notificationService.notify('danger', classifiedError.title, classifiedError.message);
    }

    if (classifiedError.kind === 'refresh_authentication') {
      console.error('Refresh Authentication Error');
      this.ngZone.run(() => this.authService.logout());

      return this.notificationService.notify('danger', classifiedError.title, classifiedError.message);
    }

    if (classifiedError.kind === 'timeout') {
      console.error('Time-out Error URL:', classifiedError.url);

      this.loggerService.logToServer(
        userId,
        `User has timed out --- request: ${classifiedError.url}` +
        ` ; accessToken exp: ${this.cryptoService.decryptText(localStorage.getItem(environment.keys.credentialsExpiry))} ;`
      );

      this.ngZone.run(() => this.authService.logout());

      return this.notificationService.notify('danger', classifiedError.title, classifiedError.message);
    }

    if (classifiedError.kind === 'http') {
      console.error('handleError', classifiedError.error);
      return this.notificationService.notify('danger', classifiedError.title, classifiedError.message);
    }

    return this.handleFatalClientError(classifiedError.error, originalError);
  }

  private handleFatalClientError(error: any, originalError: any) {
    console.error('client error');

    if (this.fatalClientErrorInProgress) {
      console.error('Fatal client error occurred while already handling a fatal client error.', originalError);
      return null;
    }

    // A fresh deployment can change lazy-chunk filenames.
    // Reload once so the browser fetches the latest app shell and bundles.
    if (this.isChunkLoadError(error) && this.tryRecoverChunkLoadError()) {
      return null;
    }

    this.fatalClientErrorInProgress = true;
    const errorMessage = this.stringifyErrorForDisplay(error);
    console.error('Fatal client error details', {
      route: this.router && this.router.url ? this.router.url : 'unknown',
      errorType: typeof error,
      errorName: error && error.constructor ? error.constructor.name : 'unknown',
      errorEventType: error && error.type ? error.type : undefined,
      errorTargetTag: error && error.target && error.target.tagName ? error.target.tagName : undefined,
      originalError: originalError,
    });

    try {
      this.ngZone.run(() => {
        this.router.navigate(['/error'], { queryParams: { error: errorMessage } })
          .catch((navigationError) => {
            console.error('Error-page navigation Promise rejected.', navigationError);
          });
      });
    } catch (navigationError) {
      console.error('Synchronous failure while starting error-page navigation.', navigationError);
    } finally {
      setTimeout(() => {
        this.fatalClientErrorInProgress = false;
      }, 0);
    }
    console.error('It happens: ', originalError);
    return null;
  }

  private shouldIgnoreHttpError(error: HttpErrorResponse): boolean {
    return !!error.url && !this.isFirstPartyApiUrl(error.url.toLowerCase());
  }

  // Check whether the failed HTTP request was sent to one of our first-party API services.
  // Third-party HTTP errors (e.g. Captcha, Google Maps, What3Words) are ignored by this
  // global handler and are expected to be handled closer to where they occur if needed.
  private isFirstPartyApiUrl(url: string): boolean {
    return url.includes(environment.apiUrl.toLowerCase())
      || url.includes(environment.competencyApiUrl.toLowerCase());
  }

  private stringifyErrorForDisplay(error: any): string {
    if (error instanceof Error) {
      return error.message || error.toString();
    }

    try {
      return typeof error === 'string' ? error : JSON.stringify(error);
    } catch (_stringifyError) {
      return String(error);
    }
  }

  private isChunkLoadError(error: any): boolean {
    const errorMessage = this.stringifyErrorForDisplay(error).toLowerCase();

    return errorMessage.includes('loading chunk') ||
      errorMessage.includes('chunkloaderror') ||
      errorMessage.includes('failed to fetch dynamically imported module');
  }

  private tryRecoverChunkLoadError(): boolean {
    if (typeof window === 'undefined' || !window.sessionStorage || !window.location) {
      return false;
    }

    const lastRetryAt = this.getChunkLoadRetryTimestamp();

    // Prevent a broken deployment from causing an infinite reload loop.
    if (lastRetryAt && (Date.now() - lastRetryAt) < this.chunkLoadRetryTtlMs) {
      return false;
    }

    window.sessionStorage.setItem(this.chunkLoadRetryStorageKey, Date.now().toString());
    this.reloadWindow();

    return true;
  }

  private reloadWindow() {
    // Reload to fetch the latest app shell and current lazy-chunk filenames after a deployment.
    window.location.reload();
  }

  private getChunkLoadRetryTimestamp(): number | null {
    const storedValue = window.sessionStorage.getItem(this.chunkLoadRetryStorageKey);

    if (!storedValue) {
      return null;
    }

    const timestamp = Number(storedValue);

    if (isNaN(timestamp)) {
      return null;
    }

    return timestamp;
  }

  private getApplicationErrorLogMessage(error: ApplicationError): string {
    const originalError = error.originalError;
    const originalErrorMessage = originalError && originalError.message
      ? originalError.message
      : String(originalError);

    return `${error.name}: ${error.message}` +
      ` :: original: ${originalErrorMessage}` +
      ` :: stack: ${this.getStackPreview(originalError)}`;
  }

  private getStackPreview(error: any): string {
    return error && error.stack
      ? error.stack.split('\n').slice(0, 2).join(' | ')
      : 'no stack';
  }
}
